import React, { useState } from 'react';
import { Collapse, Drawer, styled } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { Colors } from 'library';
import {
    Home,
    BusinessCenter,
    PeopleAlt,
    ChatBubbleOutline,
    CalendarViewMonth,
    Search,
    BarChart,
    Settings,
    AccountTree,
    Route as RouteIcon,
    Description,
    EventNote,
    Language,
    Store,
    PeopleOutline,
    Help,
    ExpandMore,
    ExpandLess,
    Mail,
    Phone,
    EventAvailable,
    People,
    WatchLater,
    AttachMoney,
    Message,
    HelpCenter,
} from '@mui/icons-material';

export type AppMode = 'recruit' | 'subs';

const SIDEBAR_WIDTH = 220;

interface NavItem {
    label: string;
    icon: React.ReactNode;
    route: string;
}

const MAIN_NAV: NavItem[] = [
    { label: 'Home', icon: <Home />, route: '/recruit' },
    { label: 'Positions', icon: <BusinessCenter />, route: '/recruit/positions' },
    { label: 'Applications', icon: <PeopleAlt />, route: '/recruit/applications' },
    { label: 'Messenger', icon: <ChatBubbleOutline />, route: '/recruit/messenger' },
    { label: 'Interviews', icon: <CalendarViewMonth />, route: '/recruit/interviews' },
    { label: 'Sourcing', icon: <Search />, route: '/recruit/sourcing' },
    { label: 'Reports', icon: <BarChart />, route: '/recruit/reports' },
];

const SETTINGS_NAV: NavItem[] = [
    { label: 'Templates', icon: <AccountTree />, route: '/recruit/settings/templates' },
    { label: 'Screening Questions', icon: <HelpCenter />, route: '/recruit/settings/screening-questions' },
    { label: 'Workflows', icon: <RouteIcon />, route: '/recruit/settings/workflows' },
    { label: 'Communication', icon: <Description />, route: '/recruit/settings/communication' },
    { label: 'Interview Settings', icon: <EventNote />, route: '/recruit/settings/interviews' },
    { label: 'Careers Page', icon: <Language />, route: '/recruit/settings/careers' },
];

const BOTTOM_NAV: NavItem[] = [
    { label: 'Manage Centers', icon: <Store />, route: '/recruit/manage-centers' },
    { label: 'Manage Users', icon: <PeopleOutline />, route: '/recruit/manage-users' },
];

const SUBS_NAV: NavItem[] = [
    { label: 'Home', icon: <Home />, route: '/home' },
    { label: 'My Jobs', icon: <EventAvailable />, route: '/jobs' },
    { label: 'My Staff', icon: <People />, route: '/providers' },
    { label: 'Timesheets', icon: <WatchLater />, route: '/timesheets' },
    { label: 'Payments', icon: <AttachMoney />, route: '/payments' },
    { label: 'Messages', icon: <Message />, route: '/messages' },
];

const SUBS_BOTTOM_NAV: NavItem[] = [
    { label: 'Manage Centers', icon: <Store />, route: '/manage-centers' },
    { label: 'Manage Users', icon: <PeopleOutline />, route: '/manage-users' },
];

export { SIDEBAR_WIDTH };

export function RecruitSidebar({
    small,
    open,
    onClose,
    mode,
    onModeChange,
}: {
    small: boolean;
    open: boolean;
    onClose: () => void;
    mode: AppMode;
    onModeChange: (mode: AppMode) => void;
}) {
    const location = useLocation();
    const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith('/recruit/settings'));

    const isActive = (route: string) => {
        if (route === '/recruit' || route === '/home') return location.pathname === route;
        return location.pathname.startsWith(route);
    };

    const handleNavClick = () => {
        if (small) onClose();
    };

    return (
        <StyledDrawer
            variant={small ? 'temporary' : 'persistent'}
            anchor="left"
            open={open}
            onClose={onClose}
            small={small}
        >
            <SidebarContent>
                <TopSection>
                    <ModeToggle>
                        <ModeButton active={mode === 'recruit'} onClick={() => onModeChange('recruit')}>
                            Recruit
                        </ModeButton>
                        <ModeButton active={mode === 'subs'} onClick={() => onModeChange('subs')}>
                            Subs
                        </ModeButton>
                    </ModeToggle>

                    {mode === 'recruit' ? (
                        <>
                            <NavSection>
                                {MAIN_NAV.map((item) => (
                                    <NavLink
                                        key={item.label}
                                        to={item.route}
                                        onClick={handleNavClick}
                                        active={isActive(item.route)}
                                    >
                                        <NavIcon active={isActive(item.route)}>{item.icon}</NavIcon>
                                        <NavLabel active={isActive(item.route)}>{item.label}</NavLabel>
                                    </NavLink>
                                ))}

                                <SettingsToggle onClick={() => setSettingsOpen(!settingsOpen)}>
                                    <NavIcon active={location.pathname.startsWith('/recruit/settings')}>
                                        <Settings />
                                    </NavIcon>
                                    <NavLabel active={location.pathname.startsWith('/recruit/settings')}>
                                        Settings
                                    </NavLabel>
                                    <ExpandIcon>
                                        {settingsOpen ? (
                                            <ExpandLess fontSize="small" />
                                        ) : (
                                            <ExpandMore fontSize="small" />
                                        )}
                                    </ExpandIcon>
                                </SettingsToggle>

                                <Collapse in={settingsOpen}>
                                    {SETTINGS_NAV.map((item) => (
                                        <NavLink
                                            key={item.label}
                                            to={item.route}
                                            onClick={handleNavClick}
                                            active={isActive(item.route)}
                                            nested
                                        >
                                            <NavIcon active={isActive(item.route)} nested>
                                                {item.icon}
                                            </NavIcon>
                                            <NavLabel active={isActive(item.route)} small>
                                                {item.label}
                                            </NavLabel>
                                        </NavLink>
                                    ))}
                                </Collapse>
                            </NavSection>

                            <Divider />

                            <NavSection>
                                {BOTTOM_NAV.map((item) => (
                                    <NavLink
                                        key={item.label}
                                        to={item.route}
                                        onClick={handleNavClick}
                                        active={isActive(item.route)}
                                    >
                                        <NavIcon active={isActive(item.route)}>{item.icon}</NavIcon>
                                        <NavLabel active={isActive(item.route)}>{item.label}</NavLabel>
                                    </NavLink>
                                ))}
                            </NavSection>
                        </>
                    ) : (
                        <>
                            <NavSection>
                                {SUBS_NAV.map((item) => (
                                    <NavLink
                                        key={item.label}
                                        to={item.route}
                                        onClick={handleNavClick}
                                        active={isActive(item.route)}
                                    >
                                        <NavIcon active={isActive(item.route)}>{item.icon}</NavIcon>
                                        <NavLabel active={isActive(item.route)}>{item.label}</NavLabel>
                                    </NavLink>
                                ))}
                            </NavSection>

                            <Divider />

                            <NavSection>
                                {SUBS_BOTTOM_NAV.map((item) => (
                                    <NavLink
                                        key={item.label}
                                        to={item.route}
                                        onClick={handleNavClick}
                                        active={isActive(item.route)}
                                    >
                                        <NavIcon active={isActive(item.route)}>{item.icon}</NavIcon>
                                        <NavLabel active={isActive(item.route)}>{item.label}</NavLabel>
                                    </NavLink>
                                ))}
                            </NavSection>
                        </>
                    )}
                </TopSection>

                <BottomSection>
                    <Divider />
                    <NavSection>
                        <ExternalLink
                            href="https://chatwoot.join-tandem.com/hc/child-care-center-help-center/en"
                            target="_blank"
                        >
                            <NavIcon active={false}>
                                <Help />
                            </NavIcon>
                            <NavLabel active={false}>FAQ</NavLabel>
                        </ExternalLink>
                    </NavSection>
                    <SupportBlock>
                        <SupportTitle>Need help?</SupportTitle>
                        <SupportRow>
                            <Mail sx={{ fontSize: 14, color: Colors.mediumGrey }} />
                            <SupportText>support@tandem.com</SupportText>
                        </SupportRow>
                        <SupportRow>
                            <Phone sx={{ fontSize: 14, color: Colors.mediumGrey }} />
                            <SupportText>1-800-TANDEM</SupportText>
                        </SupportRow>
                    </SupportBlock>
                </BottomSection>
            </SidebarContent>
        </StyledDrawer>
    );
}

const StyledDrawer = styled(Drawer)<{ small: boolean }>(({ small }) => ({
    '& .MuiDrawer-paper': {
        width: SIDEBAR_WIDTH,
        backgroundColor: Colors.white,
        borderRight: `1px solid ${Colors.lightGrey}`,
        top: small ? 50 : 60,
        height: small ? 'calc(100vh - 50px)' : 'calc(100vh - 60px)',
        zIndex: 1200,
    },
}));

const SidebarContent = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    overflowY: 'auto',
});

const TopSection = styled('div')({
    display: 'flex',
    flexDirection: 'column',
});

const BottomSection = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    paddingBottom: 8,
});

const ModeToggle = styled('div')({
    display: 'flex',
    margin: '16px 16px 8px',
    backgroundColor: Colors.lightGrey,
    borderRadius: 20,
    padding: 3,
});

const ModeButton = styled('button')<{ active: boolean }>(({ active }) => ({
    flex: 1,
    padding: '8px 0',
    border: 'none',
    borderRadius: 18,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    transition: 'all 0.2s ease',
    backgroundColor: active ? Colors.darkNavy : 'transparent',
    color: active ? Colors.white : Colors.darkNavy,
}));

const NavSection = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 8px',
});

const NavLink = styled(Link)<{ active: boolean; nested?: boolean }>(({ active, nested }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: nested ? '6px 8px 6px 24px' : '8px 8px',
    borderRadius: 6,
    textDecoration: 'none',
    cursor: 'pointer',
    backgroundColor: active ? Colors.lightGrey : 'transparent',
    '&:hover': {
        backgroundColor: Colors.lightGrey,
    },
}));

const SettingsToggle = styled('button')({
    display: 'flex',
    alignItems: 'center',
    padding: '8px 8px',
    borderRadius: 6,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    width: '100%',
    '&:hover': {
        backgroundColor: Colors.lightGrey,
    },
});

const NavIcon = styled('span')<{ active: boolean; nested?: boolean }>(({ active, nested }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    marginRight: 10,
    '& .MuiSvgIcon-root': {
        fontSize: nested ? 18 : 20,
        color: active ? Colors.darkNavy : Colors.mediumGrey,
    },
}));

const NavLabel = styled('span')<{ active: boolean; small?: boolean }>(({ active, small: isSmall }) => ({
    fontSize: isSmall ? 13 : 14,
    fontWeight: active ? 700 : 400,
    color: Colors.darkNavy,
}));

const ExpandIcon = styled('span')({
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    color: Colors.mediumGrey,
});

const Divider = styled('div')({
    height: 1,
    backgroundColor: Colors.lightGrey,
    margin: '4px 16px',
});

const ExternalLink = styled('a')({
    display: 'flex',
    alignItems: 'center',
    padding: '8px 8px',
    borderRadius: 6,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: Colors.lightGrey,
    },
});

const SupportBlock = styled('div')({
    padding: '8px 16px',
});

const SupportTitle = styled('div')({
    fontSize: 12,
    fontWeight: 700,
    color: Colors.darkNavy,
    marginBottom: 4,
});

const SupportRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
});

const SupportText = styled('span')({
    fontSize: 12,
    color: Colors.mediumGrey,
});
